import { API } from '@/api/API'
import type { EventDetails } from '@/api/Helpers'
import type { TimeInterval } from '@/helpers/Intervals'
import { normalizeSession, normalizeSessions } from '@/helpers/SessionHelpers'
import { quantitiesPayload } from '@/helpers/TicketTypeQuantities'
import { TixTime } from '@/TixTime/TixTime'
import type { Session } from '@/types/Sessions'

export function fetchFirstAvailableSession(event: EventDetails, tgIds?: string[]) {
  const args = {
    limit: 1,
    query: {
      sold_out: 'F',
      _sort: 'start_datetime',
    },
  }

  if (tgIds) {
    args.query['ticket_group_id._in'] = tgIds.join(',')
  }

  return API.getCached<'event_session'>(`events/${event.id}/sessions`, args).then((response) => {
    if (response.event_session._data.length < 1) return null
    else return normalizeSession(response.event_session._data[0], event.venue)
  })
}

export function fetchSessionsBetween(
  event: EventDetails,
  { start, end }: TimeInterval,
  tgIds: string[],
): Promise<Session[]> {
  // TODO: Update to use fetchSessionsAndPrices?
  return fetchNormalizedSessions(event, {
    'start_datetime._gte': start.format('iso'),
    'start_datetime._lt': end.format('iso'),
    'ticket_group.id._in': tgIds.join(','),
  })
}

function fetchNormalizedSessions(event: EventDetails, params: Dictionary): Promise<Session[]> {
  return API.getCached<'event_session'>(`events/${event.id}/sessions`, { query: params }).then((response) => {
    return normalizeSessions(response, event.venue)
  })
}

export function fetchSessionsOnDate(event: EventDetails, date: TixTime, tgIds: string[]): Promise<Session[]> {
  return fetchNormalizedSessions(event, {
    _ondate: date.format('YYYY-MM-DD'),
    'ticket_group.id._in': tgIds.join(','),
  })
}

export function fetchSessionsAndPrices(
  event: EventDetails,
  date: TixTime,
  quantities: TicketTypeQuantities,
): Promise<{
  sessions: Session[]
  priceSchedules: PriceSchedule[]
}> {
  const args = {
    body: quantitiesPayload(quantities),
    query: {
      _ondate: date.format('YYYY-MM-DD'),
      _sort: 'start_datetime',
      _include_sold_out: true,
    },
  }

  return API.post<'event_session' | 'price_schedule'>(`events/${event.id}/sessions`, args).then((response) => {
    return {
      sessions: normalizeSessions(response, event.venue),
      // price_schedule is only absent when ticket_types_required is empty/absent.
      // @see https://tixtrackteam.slack.com/archives/C05RQRM9JUA/p1708569368573789
      priceSchedules: response.price_schedule._data,
    }
  })
}
